import { groupBy, uniq } from "lodash-es";
import { EventStatus } from "@/graphql/gql/graphql";
import type { Maybe, OddsSelection, PopularBet } from "@/graphql/gql/graphql";
type Odds = Maybe<OddsSelection>[];

/**
 * @param headToHeadSlug like "8867/falcons-saints"
 */
export const buildHead2HeadPath = (headToHeadSlug: string) =>
  `/nfl/matchups/${headToHeadSlug}`;

export const getUniquePlayerNumberfireIds = (odds: Odds) =>
  uniq(odds.map((el) => el?.player?.numberfireId)).filter((id) => id != null);

export const groupByPlayerNumberfireId = (odds: Odds) =>
  groupBy(odds, "player.numberfireId");

/**
 * Generates the H2H matchup URL for a given game
 * Returns null if the required data is not available
 */
export const generateH2HMatchupUrl = (
  nfGameId: string,
  awayTeamShortName: string,
  homeTeamShortName: string
): string => {
  const awayTeamSlug = awayTeamShortName.toLowerCase();
  const homeTeamSlug = homeTeamShortName.toLowerCase();

  return buildHead2HeadPath(`${nfGameId}/${awayTeamSlug}-${homeTeamSlug}`);
};

export const dummyParlay: PopularBet = {
  americanOdds: 2000,
  betUrl: null,
  count: 1425,
  type: "samegameparlay",
  events: [
    {
      competitionName: "NFL",
      eventName: "KC Chiefs @ PHI Eagles",
      openDate: "2025-02-09T23:35:00.000Z",
      selections: [
        {
          formattedHandicap: null,
          handicap: 0,
          marketName: "Any Time Touchdown Scorer",
          runnerName: "Player",
        },
        {
          formattedHandicap: null,
          handicap: 0,
          marketName: "Player - Alt Rushing Yds",
          runnerName: "Player 40+ Yards",
        },
        {
          formattedHandicap: null,
          handicap: 0,
          marketName: "Player - Alt Passing Yds",
          runnerName: "Player 250+ Yards",
        },
        {
          formattedHandicap: null,
          handicap: 0,
          marketName: "Player - Alt Receptions",
          runnerName: "Player 5+ Receptions",
        },
      ],
    },
  ],
};
export const findHeadToHeadGameState = (
  /* The game state returned from the Orchestration Service doesn't update in real time.
  Usually it was updating 30+ min after kickoff.  This function bridges the gap so we can have a game status
  that changes right at kickoff. NOTE: This function only runs on page load so the page does have to be 
  after the transition to get the next status.  
  */

  gameState: EventStatus | null | undefined,
  clock: string | null | undefined,
  scheduledTime: string | null | undefined
): EventStatus => {
  const nonNullGameState: EventStatus = gameState ?? EventStatus.Scheduled;
  if (nonNullGameState === EventStatus.Final) {
    return nonNullGameState;
  }
  if (clock?.toLowerCase() === "event over") {
    return EventStatus.Final;
  }
  if (nonNullGameState === EventStatus.Live) {
    return nonNullGameState;
  }
  if (!scheduledTime) {
    return nonNullGameState;
  }
  const gameTime = new Date(scheduledTime);
  const currentTime = new Date();
  const timeToGame = gameTime.getTime() - currentTime.getTime();
  if (isNaN(timeToGame)) {
    return nonNullGameState;
  }
  if (clock == null && timeToGame > 0) {
    return nonNullGameState;
  }

  if (
    nonNullGameState === EventStatus.Scheduled &&
    (clock != null || timeToGame < 0)
  ) {
    return EventStatus.Live;
  }

  return nonNullGameState;
};
